<template>
  <div class="customers-page py-3">
    <header class="customers-toolbar">
      <div class="customers-toolbar__title">
        <span class="customers-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Karnataka Customers</h1>
          <p>Browse and search every customer with a Karnataka address</p>
        </div>
      </div>
      <div class="customers-toolbar__actions">
        <span v-if="lastUpdatedAt && !isLoading" class="customers-updated">Updated {{ lastUpdatedLabel }}</span>
        <CButton size="sm" class="refresh-btn" :class="{ 'is-loading': isLoading }" :disabled="isLoading"
          @click="reload">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round" class="refresh-btn__icon" aria-hidden="true">
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
          <span>{{ isLoading ? 'Refreshing…' : 'Refresh' }}</span>
        </CButton>
 
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="customers-alert">{{ errorMessage }}</CAlert>

    <div class="summary-tiles">
      <div v-for="card in summaryCards" :key="card.key" :class="['summary-tile', `summary-tile--${card.key}`]">
        <span class="summary-tile__label">{{ card.label }}</span>
        <span class="summary-tile__value">{{ card.value }}</span>
      </div>
    </div>

    <section class="customers-section">
      <header class="customers-section__head">
        <h2>{{ isFilterOrSearchActive ? 'Search Results' : 'All Karnataka Customers' }}</h2>
        <span v-if="!isLoading && rows.length" class="customers-count">{{ paginationLabel }}</span>
      </header>

      <div class="customers-section__body">
        <div class="customers-filters">
          <div class="customers-filter customers-filter--search">
            <label class="customers-filter__label">Search</label>
            <form class="customers-search-wrap" @submit.prevent="onSearchSubmit">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"
                stroke-linecap="round" stroke-linejoin="round" class="customers-search-icon" aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input v-model.trim="searchTerm" type="text" class="customers-input"
                placeholder="Search by name or phone…" />
              <button v-if="searchTerm || isSearchActive" type="button" class="customers-search-clear"
                aria-label="Clear search" @click="clearSearch">×</button>
            </form>
          </div>
          <div class="customers-filter">
            <label class="customers-filter__label">District</label>
            <AppDropdown v-model="filters.district" :options="districtOptions" searchable
              search-placeholder="Search district…" placeholder="All districts" />
          </div>
          <div class="customers-filter customers-filter--action">
            <label class="customers-filter__label" aria-hidden="true">&nbsp;</label>
            <button type="button" class="customers-apply-btn" :disabled="isLoading" @click="applyFilters">Apply</button>
          </div>
          <div class="customers-filter customers-filter--action">
            <label class="customers-filter__label" aria-hidden="true">&nbsp;</label>
            <button type="button" class="customers-reset-btn" :disabled="isLoading || !isFilterOrSearchActive"
              @click="clearAll">Reset</button>
          </div>
        </div>
      </div>

      <div v-if="isLoading" class="customers-section__body customers-loading">
        <CSpinner size="sm" class="mr-2" />
        <span>Loading customers…</span>
      </div>

      <div v-else-if="!rows.length" class="customers-section__body customers-empty">
        <div class="customers-empty__title">
          {{ isFilterOrSearchActive ? 'No matching customers' : 'No Karnataka customers yet' }}
        </div>
        <div class="customers-empty__hint">
          <template v-if="isFilterOrSearchActive">
            Try adjusting your search or district —
            <button type="button" class="customers-empty__link" @click="clearAll">clear all</button>.
          </template>
          <template v-else>
            Customers with a Karnataka address will appear here.
          </template>
        </div>
      </div>

      <div v-else>
        <div class="customers-table-wrap">
          <table class="customers-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Phone</th>
                <th>District</th>
                <th class="col-num">Orders</th>
                <th class="col-num">Bottles</th>
                <th class="col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(customer, index) in rows" :key="customer.id || `row-${index}`" class="customer-row">
                <td class="col-customer">
                  <div class="customer-cell">
                    <div class="customer-avatar" :style="{ background: avatarColor(customer) }">
                      {{ initials(customer.name) }}
                    </div>
                    <div>
                      <div class="customer-name">{{ customer.name || 'Unnamed' }}</div>
                      <div v-if="customer.id" class="customer-sub">#{{ customer.id }}</div>
                    </div>
                  </div>
                </td>
                <td class="col-phone">
                  <span v-if="customer.phone" class="customer-phone">{{ customer.phone }}</span>
                  <span v-else class="customer-dim">—</span>
                </td>
                <td class="col-district">
                  <span v-if="districtLabel(customer)" class="district-pill">{{ districtLabel(customer) }}</span>
                  <span v-else class="customer-dim">—</span>
                </td>
                <td class="col-num">{{ ordersCount(customer) }}</td>
                <td class="col-num">{{ Number(customer.bottles || 0) }}</td>
                <td class="col-actions">
                  <div class="kc-row-actions">
                    <button type="button" class="kc-act kc-act--view" @click="openDetail(customer)">
                      <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2"
                        stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                        <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                      <span>View</span>
                    </button>
                    <button type="button" class="kc-act kc-act--call" :disabled="!customer.phone"
                      title="Open in Tele Sales" @click="goToTelesales(customer.phone)">
                      <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2"
                        stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                        <path
                          d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
                      </svg>
                      <span>Call</span>
                    </button>
                    <button type="button" class="kc-act kc-act--copy" :disabled="!customer.phone"
                      :title="copiedId === customer.id ? 'Copied!' : 'Copy phone'"
                      @click="copyPhone(customer.phone, customer.id)">
                      <svg v-if="copiedId === customer.id" viewBox="0 0 24 24" width="13" height="13" fill="none"
                        stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"
                        aria-hidden="true">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      <svg v-else viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor"
                        stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="totalPages > 1" class="customers-pagination">
          <AppPagination :value="currentPage" :pages="totalPages" :disabled="isLoading" @change="onPageChange" />
        </div>
      </div>
    </section>

    <transition name="kc-modal-fade">
      <div v-if="showDetailModal && detailCustomer" class="kc-modal" role="dialog" aria-modal="true"
        aria-labelledby="kc-modal-title" @mousedown.self="closeDetail">
        <div class="kc-modal__panel" @mousedown.stop>
          <header class="kc-modal__head">
            <div class="kc-modal__id">
              <div class="kc-modal__avatar" :style="{ background: avatarColor(detailCustomer) }">{{
                initials(detailCustomer.name) }}</div>
              <div class="kc-modal__id-text">
                <h3 id="kc-modal-title" class="kc-modal__name">{{ detailCustomer.name || 'Unnamed' }}</h3>
                <div class="kc-modal__meta">
                  <span v-if="detailCustomer.phone" class="kc-modal__phone">{{ detailCustomer.phone }}</span>
                  <span v-if="districtLabel(detailCustomer)" class="district-pill">{{ districtLabel(detailCustomer)
                    }}</span>
                </div>
              </div>
            </div>
            <button type="button" class="kc-modal__close" aria-label="Close" @click="closeDetail">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2"
                stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </header>

          <div class="kc-modal__toolbar">
            <button type="button" class="kc-modal__btn kc-modal__btn--call" :disabled="!detailCustomer.phone"
              @click="goToTelesales(detailCustomer.phone)">
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2"
                stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path
                  d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
              <span>Call in Tele Sales</span>
            </button>
            <button type="button" class="kc-modal__btn kc-modal__btn--copy" :disabled="!detailCustomer.phone"
              @click="copyPhone(detailCustomer.phone, detailCustomer.id)">
              <svg v-if="copiedId === detailCustomer.id" viewBox="0 0 24 24" width="15" height="15" fill="none"
                stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"
                aria-hidden="true">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <svg v-else viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2"
                stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
              <span>{{ copiedId === detailCustomer.id ? 'Copied!' : 'Copy phone' }}</span>
            </button>
          </div>

          <div class="kc-modal__body">
            <div v-if="detailLoading" class="kc-modal__loading">
              <CSpinner size="sm" class="mr-2" />
              <span>Loading history…</span>
            </div>
            <div v-else class="kc-history-grid">
              <div class="kc-history-col">
                <h4 class="kc-history-title">Recent Orders</h4>
                <div v-if="detailOrders.length" class="kc-history-list">
                  <div v-for="order in detailOrders" :key="`o-${order.id}`" class="kc-history-item">
                    <div class="kc-history-item__line">
                      <span class="kc-history-item__strong">{{ order.order_number || ('#' + order.id) }}</span>
                      <span class="kc-history-item__amount">{{ formatCurrency(order.grand_total) }}</span>
                    </div>
                    <div class="kc-history-item__sub">
                      {{ orderDateDisplay(order) }}
                      <template v-if="order.payment_status">
                        <span class="kc-dot">·</span>
                        <span :class="paymentStatusClass(order.payment_status)">{{ formatLabel(order.payment_status)
                          }}</span>
                      </template>
                      <template v-if="order.order_status">
                        <span class="kc-dot">·</span>{{ formatLabel(order.order_status) }}
                      </template>
                    </div>
                  </div>
                </div>
                <div v-else class="kc-history-empty">No orders found.</div>
              </div>

              <div class="kc-history-col">
                <h4 class="kc-history-title">Recent Calls</h4>
                <div v-if="detailCalls.length" class="kc-history-list">
                  <div v-for="call in detailCalls" :key="`c-${call.id}`" class="kc-history-item">
                    <div class="kc-history-item__line">
                      <span class="kc-history-item__date">{{ formatCallDate(call.created_at) }}</span>
                      <span class="kc-pill">{{ formatLabel(call.status) }}</span>
                    </div>
                    <div class="kc-history-item__sub">{{ call.caller || 'Unknown caller' }}</div>
                  </div>
                </div>
                <div v-else class="kc-history-empty">No calls found.</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios'
import AppPagination from '@/components/AppPagination.vue'
import AppDropdown from '@/components/AppDropdown.vue'

export default {
  name: 'KarnatakaCustomers',
  components: { AppPagination, AppDropdown },
  data() {
    return {
      rows: [],
      searchTerm: '',
      appliedSearchTerm: '',
      isSearchActive: false,
      currentPage: 1,
      totalPages: 1,
      totalRecords: 0,
      isLoading: false,
      isExporting: false,
      errorMessage: '',
      lastUpdatedAt: null,
      filters: {
        district: ''
      },
      appliedFilters: {
        district: ''
      },
      districts: [],
      // Customer detail modal (recent orders + recent calls)
      showDetailModal: false,
      detailCustomer: null,
      detailOrders: [],
      detailCalls: [],
      detailLoading: false,
      // Id of the row/customer whose phone was just copied — drives the
      // transient "Copied!" affordance. Cleared after a short timeout.
      copiedId: null,
      copiedTimer: null
    }
  },
  beforeDestroy() {
    if (this.copiedTimer) clearTimeout(this.copiedTimer)
  },
  computed: {
    hasActiveFilters() {
      return !!this.appliedFilters.district
    },
    isFilterOrSearchActive() {
      return this.isSearchActive || this.hasActiveFilters
    },
    districtOptions() {
      return [{ value: '', label: 'All districts' }].concat(
        (this.districts || []).map((v) => ({ value: v, label: v }))
      )
    },
    lastUpdatedLabel() {
      if (!this.lastUpdatedAt) return ''
      const hours = String(this.lastUpdatedAt.getHours()).padStart(2, '0')
      const minutes = String(this.lastUpdatedAt.getMinutes()).padStart(2, '0')
      return `${hours}:${minutes}`
    },
    summaryCards() {
      const districts = new Set(
        this.rows.map((row) => this.districtLabel(row)).filter(Boolean)
      )
      const pageOrders = this.rows.reduce((sum, row) => sum + this.ordersCount(row), 0)
      const pageBottles = this.rows.reduce((sum, row) => sum + Number(row.bottles || 0), 0)
      const total = this.totalRecords || this.rows.length

      return [
        { key: 'total', label: this.isFilterOrSearchActive ? 'Matches' : 'Total Customers', value: total },
        { key: 'orders', label: 'Orders (Page)', value: pageOrders },
        { key: 'bottles', label: 'Bottles (Page)', value: pageBottles },
        { key: 'districts', label: 'Districts (Page)', value: districts.size }
      ]
    },
    paginationLabel() {
      const total = this.totalRecords
      const noun = total === 1 ? 'customer' : 'customers'
      if (this.isFilterOrSearchActive && !total) {
        return `${this.rows.length} on page`
      }
      if (!total || this.totalPages <= 1) {
        return `${this.rows.length} ${this.rows.length === 1 ? 'customer' : 'customers'}`
      }
      const perPage = this.rows.length
      const start = (this.currentPage - 1) * perPage + 1
      const end = Math.min(start + perPage - 1, total)
      return `${start}–${end} of ${total} ${noun}`
    }
  },
  created() {
    this.fetchDistricts()
    this.fetchContacts(1)
  },
  methods: {
    token() {
      return localStorage.getItem('api_token')
    },
    initials(name) {
      if (!name) return '?'
      const parts = String(name).trim().split(/\s+/)
      const first = parts[0] ? parts[0][0] : ''
      const second = parts.length > 1 ? parts[parts.length - 1][0] : ''
      return (first + second).toUpperCase() || '?'
    },
    avatarColor(row) {
      const palette = [
        'linear-gradient(135deg, #0e5b46 0%, #16a07a 100%)',
        'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        'linear-gradient(135deg, #6d28d9 0%, #a855f7 100%)',
        'linear-gradient(135deg, #b45309 0%, #f59e0b 100%)',
        'linear-gradient(135deg, #be185d 0%, #ec4899 100%)',
        'linear-gradient(135deg, #115e59 0%, #14b8a6 100%)'
      ]
      const seed = String(row?.name || row?.id || row?.phone || '')
      let hash = 0
      for (let i = 0; i < seed.length; i++) {
        hash = (hash * 31 + seed.charCodeAt(i)) >>> 0
      }
      return palette[hash % palette.length]
    },
    districtLabel(row) {
      const addresses = Array.isArray(row?.address) ? row.address : []
      // Prefer the Karnataka address row's district — a contact can carry
      // addresses in several states, and this page is about the Karnataka one.
      const karnataka = addresses.find((a) => String(a?.state || '').trim().toLowerCase() === 'karnataka')
      const address = karnataka || addresses[0] || null
      const district = address?.district || row?.district || ''
      return district ? String(district).trim() : ''
    },
    ordersCount(row) {
      if (Array.isArray(row?.order)) return row.order.length
      if (typeof row?.orders_count === 'number') return row.orders_count
      return 0
    },
    formatCurrency(value) {
      const numeric = Number(value || 0)
      return Number.isFinite(numeric) ? `₹${numeric.toFixed(2)}` : '₹0.00'
    },
    formatLabel(value) {
      return String(value || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    },
    paymentStatusClass(status) {
      const s = String(status || '').toLowerCase()
      if (s === 'paid') return 'kc-text-success'
      if (s.includes('pending')) return 'kc-text-warning'
      return ''
    },
    // Order.created_at is a Unix timestamp (the "1970" trap); prefer the
    // ISO order_date, fall back to created_at × 1000.
    orderDateDisplay(order) {
      if (!order) return '—'
      const fmt = (v) => {
        const d = new Date(v)
        if (isNaN(d.getTime())) return '—'
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
        return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`
      }
      if (order.order_date) return fmt(order.order_date)
      const ts = Number(order.created_at)
      if (Number.isFinite(ts) && ts > 0) return fmt(ts * 1000)
      return '—'
    },
    formatCallDate(raw) {
      if (!raw) return '—'
      const d = new Date(raw)
      if (isNaN(d.getTime())) return String(raw)
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`
    },
    // Open the Tele Sales form with this customer's number prefilled —
    // TeleSales reads ?phone= on mount, populates the field and auto-searches.
    goToTelesales(phone) {
      const number = String(phone || '').trim()
      if (!number) return
      this.$router.push({ path: '/telesales', query: { phone: number } })
    },
    async copyPhone(phone, id) {
      const number = String(phone || '').trim()
      if (!number) return
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(number)
        } else {
          const ta = document.createElement('textarea')
          ta.value = number
          ta.style.position = 'fixed'
          ta.style.opacity = '0'
          document.body.appendChild(ta)
          ta.select()
          document.execCommand('copy')
          document.body.removeChild(ta)
        }
      } catch (e) {
        console.log(e)
        return
      }
      this.copiedId = id != null ? id : number
      if (this.copiedTimer) clearTimeout(this.copiedTimer)
      this.copiedTimer = setTimeout(() => { this.copiedId = null }, 1800)
    },
    openDetail(customer) {
      if (!customer) return
      this.detailCustomer = customer
      this.detailOrders = []
      this.detailCalls = []
      this.showDetailModal = true
      this.fetchDetail(customer.id)
    },
    closeDetail() {
      this.showDetailModal = false
      this.detailCustomer = null
      this.detailOrders = []
      this.detailCalls = []
    },
    fetchDetail(id) {
      if (!id) return
      this.detailLoading = true
      const params = { token: this.token() }
      Promise.all([
        axios.get(`${this.$apiAdress}/api/contacts/orders/${id}`, { params }),
        axios.get(`${this.$apiAdress}/api/contacts/calls/${id}`, { params })
      ])
        .then(([ordersRes, callsRes]) => {
          const ordersPayload = ordersRes.data || {}
          const callsPayload = callsRes.data || {}
          this.detailOrders = Array.isArray(ordersPayload.data) ? ordersPayload.data : (Array.isArray(ordersPayload) ? ordersPayload : [])
          this.detailCalls = Array.isArray(callsPayload.data) ? callsPayload.data : (Array.isArray(callsPayload) ? callsPayload : [])
        })
        .catch((error) => {
          console.log(error)
          this.detailOrders = []
          this.detailCalls = []
        })
        .finally(() => {
          this.detailLoading = false
        })
    },
    reload() {
      this.fetchContacts(this.currentPage || 1)
    },
    queryParams() {
      const params = { token: this.token() }
      if (this.appliedSearchTerm) params.search = this.appliedSearchTerm
      if (this.appliedFilters.district) params.district = this.appliedFilters.district
      return params
    },
    fetchDistricts() {
      axios.get(`${this.$apiAdress}/api/karnataka-customers/districts`, { params: { token: this.token() } })
        .then((response) => {
          const payload = response.data || {}
          this.districts = Array.isArray(payload.districts) ? payload.districts : []
        })
        .catch((error) => {
          console.log(error)
        })
    },
    fetchContacts(page) {
      this.errorMessage = ''
      this.isLoading = true

      const params = { ...this.queryParams(), page: page || 1 }

      axios.get(`${this.$apiAdress}/api/karnataka-customers`, { params })
        .then((response) => {
          const payload = response.data || {}
          const data = Array.isArray(payload.data) ? payload.data : []
          this.rows = data
          this.currentPage = Number(payload.current_page || page || 1)
          this.totalPages = Number(payload.last_page || 1)
          this.totalRecords = Number(payload.total || data.length)
          this.isSearchActive = !!this.appliedSearchTerm
          this.lastUpdatedAt = new Date()
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = 'Unable to load Karnataka customers.'
          this.rows = []
          this.totalRecords = 0
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    onSearchSubmit() {
      this.appliedSearchTerm = this.searchTerm
      this.fetchContacts(1)
    },
    clearSearch() {
      this.searchTerm = ''
      this.appliedSearchTerm = ''
      this.isSearchActive = false
      this.fetchContacts(1)
    },
    applyFilters() {
      this.appliedSearchTerm = this.searchTerm
      this.appliedFilters = {
        district: this.filters.district
      }
      this.fetchContacts(1)
    },
    clearAll() {
      this.searchTerm = ''
      this.appliedSearchTerm = ''
      this.isSearchActive = false
      this.filters = { district: '' }
      this.appliedFilters = { district: '' }
      this.fetchContacts(1)
    },
    exportCustomers() {
      if (this.isExporting) return
      this.isExporting = true
      this.errorMessage = ''

      axios.get(`${this.$apiAdress}/api/karnataka-customers/export`, {
        params: this.queryParams(),
        responseType: 'blob'
      })
        .then((response) => {
          const blob = new Blob([response.data], {
            type: response.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          })
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          const stamp = new Date().toISOString().slice(0, 10)
          link.href = url
          link.download = `karnataka_customers_${stamp}.xlsx`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = 'Unable to export Karnataka customers.'
        })
        .finally(() => {
          this.isExporting = false
        })
    },
    onPageChange(page) {
      const target = Number(page || 1)
      if (target === this.currentPage) return
      this.currentPage = target
      this.fetchContacts(target)
    }
  }
}
</script>

<style scoped>
.customers-page {
  padding: 0.25rem 0;
}

/* ── Toolbar ── */
.customers-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
}

.customers-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.customers-toolbar__accent {
  width: 4px;
  height: 36px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
  flex-shrink: 0;
}

.customers-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.customers-toolbar__title p {
  margin: 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.customers-toolbar__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.65rem;
}

.customers-updated {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
}

.customers-toolbar__actions>>>.btn.refresh-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 34px;
  padding: 0 0.85rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.customers-toolbar__actions>>>.btn.refresh-btn:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.12);
}

.customers-toolbar__actions>>>.btn.refresh-btn:disabled {
  opacity: 1;
  background: #f8fafc;
  color: #4b5563;
  cursor: progress;
}

.refresh-btn__icon {
  flex-shrink: 0;
  display: block;
  color: #16a07a;
  transition: transform 0.4s ease;
}

.customers-toolbar__actions>>>.btn.refresh-btn:hover:not(:disabled) .refresh-btn__icon {
  transform: rotate(60deg);
}

.customers-toolbar__actions>>>.btn.refresh-btn.is-loading .refresh-btn__icon {
  animation: refresh-spin 0.9s linear infinite;
  color: #16a07a;
}

@keyframes refresh-spin {
  to {
    transform: rotate(360deg);
  }
}

/* ── Export button ── */
.customers-toolbar__actions>>>.btn.export-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 34px;
  padding: 0 0.85rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.customers-toolbar__actions>>>.btn.export-btn:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.12);
}

.customers-toolbar__actions>>>.btn.export-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.customers-toolbar__actions>>>.btn.export-btn.is-loading {
  cursor: progress;
}

.export-btn__icon {
  flex-shrink: 0;
  display: block;
  color: #16a07a;
}

/* ── Filters row ── */
.customers-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  align-items: flex-end;
}

.customers-filter {
  display: flex;
  flex-direction: column;
  flex: 1 1 220px;
  min-width: 0;
}

.customers-filter--search {
  flex: 2 1 320px;
}

.customers-filter--action {
  flex: 0 0 auto;
  min-width: 110px;
}

.customers-filter__label {
  display: block;
  margin-bottom: 0.3rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
}

.customers-apply-btn,
.customers-reset-btn {
  width: 100%;
  height: 36px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease, background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.customers-apply-btn {
  border: 0;
  color: #ffffff;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
}

.customers-apply-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
}

.customers-reset-btn {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
}

.customers-reset-btn:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
}

.customers-apply-btn:disabled,
.customers-reset-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.customers-apply-btn:focus,
.customers-reset-btn:focus {
  outline: none;
}

.customers-apply-btn:focus-visible,
.customers-reset-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

/* ── Alert ── */
.customers-alert {
  margin: 0 0 0.75rem;
  border-radius: 10px;
}

/* ── Summary tiles ── */
.summary-tiles {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.summary-tile {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  border-left-width: 4px;
}

.summary-tile--total {
  border-left-color: #1f2a44;
}

.summary-tile--orders {
  border-left-color: #16a07a;
}

.summary-tile--bottles {
  border-left-color: #6d28d9;
}

.summary-tile--districts {
  border-left-color: #0ea5e9;
}

.summary-tile__label {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #4b5563;
}

.summary-tile__value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2a44;
}

/* ── Section card ── */
.customers-section {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  margin-bottom: 1rem;
}

.customers-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.7rem 1rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
  border-radius: 12px 12px 0 0;
}

.customers-section__head h2 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.customers-count {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  background: #f1f5f9;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
}

.customers-section__body {
  padding: 1rem;
}

/* ── Search ── */
.customers-search-wrap {
  position: relative;
  width: 100%;
}

.customers-search-icon {
  position: absolute;
  top: 50%;
  left: 0.75rem;
  transform: translateY(-50%);
  color: #6b7280;
  pointer-events: none;
}

.customers-input {
  width: 100%;
  height: 36px;
  padding: 0.3rem 2.25rem 0.3rem 2.1rem;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  background: #ffffff;
  font-size: 0.85rem;
  color: #2f3640;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.customers-input:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.customers-search-clear {
  position: absolute;
  top: 50%;
  right: 0.5rem;
  transform: translateY(-50%);
  width: 22px;
  height: 22px;
  border: 0;
  border-radius: 50%;
  background: #e5e9f2;
  color: #4b5563;
  font-size: 1rem;
  line-height: 1;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, color 0.15s ease;
}

.customers-search-clear:hover {
  background: #d1d5db;
  color: #1f2a44;
}

.customers-search-clear:focus {
  outline: none;
}

.customers-search-clear:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

/* ── Loading / empty ── */
.customers-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 3rem 1rem;
  color: #6b7280;
  font-size: 0.85rem;
}

.customers-empty {
  text-align: center;
  padding: 3rem 1rem;
}

.customers-empty__title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  margin-bottom: 0.25rem;
}

.customers-empty__hint {
  font-size: 0.82rem;
  color: #6b7280;
}

.customers-empty__link {
  border: 0;
  background: transparent;
  padding: 0;
  color: #0e5b46;
  font-weight: 600;
  cursor: pointer;
}

.customers-empty__link:hover {
  text-decoration: underline;
}

/* ── Table ── */
.customers-table-wrap {
  overflow-x: auto;
}

.customers-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.82rem;
}

.customers-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #fbfcfe;
  border-top: 0;
  border-bottom: 2px solid #e5e9f2;
  padding: 0.65rem 0.9rem;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #374151;
  white-space: nowrap;
  text-align: left;
}

.customers-table tbody tr {
  transition: background-color 0.12s ease;
}

.customers-table tbody tr:hover {
  background: #f8fafc;
}

.customers-table td {
  padding: 0.85rem 0.9rem;
  border-bottom: 1px solid #eef1f7;
  vertical-align: middle;
  color: #374151;
}

.col-customer {
  min-width: 220px;
}

.col-phone {
  min-width: 140px;
}

.col-district {
  min-width: 140px;
}

.col-num {
  width: 92px;
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  color: #1f2a44;
}

.col-actions {
  width: 1%;
  text-align: right;
  white-space: nowrap;
}

/* ── Customer cell ── */
.customer-cell {
  display: flex;
  align-items: center;
  gap: 0.7rem;
}

.customer-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-weight: 700;
  font-size: 0.78rem;
  letter-spacing: 0.02em;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(15, 23, 42, 0.12);
}

.customer-name {
  font-size: 0.88rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.25;
}

.customer-sub {
  font-size: 0.73rem;
  color: #6b7280;
  line-height: 1.4;
}

.customer-phone {
  font-size: 0.85rem;
  color: #1f2a44;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.01em;
}

.customer-dim {
  color: #9ca3af;
  font-size: 0.82rem;
}

/* ── District pill ── */
.district-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.18rem 0.6rem;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: capitalize;
  white-space: nowrap;
  background: #e0edff;
  color: #1e40af;
}

/* ── Row actions ── */
.kc-row-actions {
  display: inline-flex;
  gap: 0.4rem;
  justify-content: flex-end;
}

.kc-act {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  height: 28px;
  padding: 0 0.6rem;
  border-radius: 6px;
  border: 1px solid #e5e9f2;
  background: #ffffff;
  color: #1f2a44;
  font-size: 0.76rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}

.kc-act svg {
  flex: 0 0 auto;
}

.kc-act:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.kc-act--view:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
}

.kc-act--call:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f0fbf6;
}

.kc-act--copy {
  padding: 0 0.5rem;
}

.kc-act--copy:hover:not(:disabled) {
  border-color: #3b82f6;
  color: #1e40af;
  background: #eff6ff;
}

.kc-act:focus {
  outline: none;
}

.kc-act::-moz-focus-inner {
  border: 0;
}

.kc-act:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

/* ── Detail modal ── */
.kc-modal {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  background: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
}

.kc-modal__panel {
  width: 100%;
  max-width: 640px;
  max-height: calc(100vh - 2.5rem);
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid #e5e9f2;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.28);
  overflow: hidden;
}

.kc-modal__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.9rem 1.1rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
}

.kc-modal__id {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  min-width: 0;
}

.kc-modal__avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-weight: 700;
  font-size: 0.85rem;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(15, 23, 42, 0.12);
}

.kc-modal__id-text {
  min-width: 0;
}

.kc-modal__name {
  margin: 0;
  font-size: 1.02rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.kc-modal__meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.2rem;
}

.kc-modal__phone {
  font-size: 0.82rem;
  font-weight: 600;
  color: #1f2a44;
  font-variant-numeric: tabular-nums;
}

.kc-modal__close {
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 0;
  background: transparent;
  color: #6b7280;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.kc-modal__close:hover {
  background: #f3f4f6;
  color: #1f2a44;
}

.kc-modal__close:focus {
  outline: none;
}

.kc-modal__close:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.kc-modal__toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.75rem 1.1rem;
  border-bottom: 1px solid #eef1f7;
}

.kc-modal__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 36px;
  padding: 0 0.95rem;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease, background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.kc-modal__btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.kc-modal__btn--call {
  border: 0;
  color: #ffffff;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
}

.kc-modal__btn--call:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
}

.kc-modal__btn--copy {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
}

.kc-modal__btn--copy:hover:not(:disabled) {
  border-color: #3b82f6;
  color: #1e40af;
  background: #eff6ff;
}

.kc-modal__btn:focus {
  outline: none;
}

.kc-modal__btn::-moz-focus-inner {
  border: 0;
}

.kc-modal__btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.kc-modal__body {
  padding: 1rem 1.1rem 1.1rem;
  overflow-y: auto;
  background: #fbfcfe;
}

.kc-modal__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 2.5rem 1rem;
  color: #6b7280;
  font-size: 0.85rem;
}

.kc-history-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.kc-history-col {
  min-width: 0;
}

.kc-history-title {
  margin: 0 0 0.5rem;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
}

.kc-history-list {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  max-height: 360px;
  overflow-y: auto;
  padding-right: 0.2rem;
}

.kc-history-item {
  padding: 0.55rem 0.7rem;
  border: 1px solid #e6eaf3;
  border-radius: 8px;
  background: #ffffff;
}

.kc-history-item__line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.kc-history-item__strong {
  font-size: 0.84rem;
  font-weight: 700;
  color: #1f2a44;
}

.kc-history-item__date {
  font-size: 0.82rem;
  font-weight: 600;
  color: #1f2a44;
}

.kc-history-item__amount {
  font-size: 0.88rem;
  font-weight: 700;
  color: #0e5b46;
  white-space: nowrap;
}

.kc-history-item__sub {
  margin-top: 0.2rem;
  font-size: 0.76rem;
  color: #6b7280;
}

.kc-dot {
  margin: 0 0.25rem;
  color: #cbd5e1;
}

.kc-text-success {
  color: #0e5b46;
  font-weight: 600;
}

.kc-text-warning {
  color: #b45309;
  font-weight: 600;
}

.kc-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 600;
  white-space: nowrap;
  background: #eef1f7;
  color: #4b5563;
}

.kc-history-empty {
  padding: 0.85rem;
  text-align: center;
  font-size: 0.8rem;
  color: #6b7280;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  background: #f8fafc;
}

.kc-modal-fade-enter-active,
.kc-modal-fade-leave-active {
  transition: opacity 0.18s ease;
}

.kc-modal-fade-enter,
.kc-modal-fade-leave-to {
  opacity: 0;
}

/* ── Pagination ── */
.customers-pagination {
  padding: 0.75rem 1rem;
  border-top: 1px solid #eef1f7;
}

/* ── Responsive ── */
@media (max-width: 991px) {
  .summary-tiles {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  .customers-toolbar {
    align-items: flex-start;
  }

  .summary-tiles {
    grid-template-columns: 1fr 1fr;
  }

  .kc-history-grid {
    grid-template-columns: 1fr;
  }
}
</style>
